/* We can disable import/no-cycle here because we know that
  api only depends on the User module. */
// eslint-disable-next-line import/no-cycle
import api from '@/api';

const localState = {
  landownerReport: {},
  loading: false,
  activitiesErrors: [],
  stumpageErrors: [],
  errorsLoading: false,
  errors: {
    generalDetails: [],
    involvement: [],
    activities: [],
    stumpage: [],
  },
  showSubmittedModal: false,
  isAdminEditing: false,
  editorComments: {
    generalDetails: '',
    involvement: '',
    activities: '',
    stumpage: '',
  },
};

const actions = {
  async getLandownerReport({ commit }, id) {
    if (!id) return;
    commit('SET_LOADING', true);
    await api.get(`/landownerReport/${id}`).then((response) => {
      commit('SET_LANDOWNER_REPORT', response.data);
      commit('SET_LOADING', false);
    }).catch(() => {
      commit('SET_LOADING', false);
    });
  },
  async getLandownerReportNoReload({ commit }, id) {
    if (!id) return;
    await api.get(`/landownerReport/${id}`).then((response) => {
      commit('SET_LANDOWNER_REPORT', response.data);
    });
  },
  async create(_context, { data, submittedCallback }) {
    await api.post('/landownerReport/create', data)
      .then(async (response) => {
        if (submittedCallback) submittedCallback(response.data);
      });
  },
  async viewed(_context, { id, submittedCallback }) {
    await api.post(`/landownerReport/${id}/viewed`)
      .then(async (response) => {
        if (submittedCallback) submittedCallback(response.data);
      });
  },
  async updateGeneralDetails({ state, dispatch }, { data, submittedCallback }) {
    await api.patch(`/landownerReport/${state.landownerReport.id}/updateGeneralDetails`, data)
      .then(async (response) => {
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      });
  },
  async updateInvolvement({ state, dispatch }, { data, submittedCallback }) {
    await api.patch(`/landownerReport/${state.landownerReport.id}/updateInvolvement`, data)
      .then(async (response) => {
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      });
  },
  async updateActivities({ state, commit, dispatch }, { data, submittedCallback }) {
    await api.patch(`/landownerReport/${state.landownerReport.id}/updateActivities`, data)
      .then(async (response) => {
        commit('SET_ACTIVITIES_ERRORS', []);
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      })
      .catch((error) => {
        commit('SET_ACTIVITIES_ERRORS', error.response ? error.response.data : [error.message]);
        throw error;
      });
  },
  async updateStumpages({ state, commit, dispatch }, { data, submittedCallback }) {
    await api.patch(`/landownerReport/${state.landownerReport.id}/updateStumpages`, data)
      .then(async (response) => {
        commit('SET_STUMPAGE_ERRORS', []);
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      })
      .catch((error) => {
        commit('SET_STUMPAGE_ERRORS', error.response ? Object.values(error.response.data).reduce((a, b) => a.concat(b)) : [error.message]);
        throw error;
      });
  },
  async validate({ commit, state, getters }) {
    if (!getters.started) return;
    commit('SET_ERRORS_LOADING', true);
    await api.get(`landownerReport/${state.landownerReport.id}/validate`)
      .then((response) => {
        commit('SET_ERRORS', response.data);
        commit('SET_ERRORS_LOADING', false);
      });
  },
  async submit({ commit, dispatch }, id) {
    await api.post(`/landownerReport/${id}/submit`)
      .then(async () => {
        await dispatch('getLandownerReportNoReload', id);
        commit('SET_SHOW_SUBMITTED_MODAL', true);
      });
  },
  async withdraw({ _ }, reportId) {
    await api.post(`/landownerReport/${reportId}/withdraw`);
  },
  hideSubmittedModal({ commit }) {
    commit('SET_SHOW_SUBMITTED_MODAL', false);
  },
  async delete({ _ }, reportId) {
    await api.post(`/landownerReport/${reportId}/delete`);
  },

  // Admin Edit actions
  startAdminEdit({ commit, state }) {
    commit('SET_ADMIN_EDITING', true);
    commit('SET_EDITOR_COMMENTS', {
      generalDetails: state.landownerReport.editorCommentGeneralDetails || '',
      involvement: state.landownerReport.editorCommentInvolvement || '',
      activities: state.landownerReport.editorCommentActivities || '',
      stumpage: state.landownerReport.editorCommentStumpage || '',
    });
  },
  stopAdminEdit({ commit }) {
    commit('SET_ADMIN_EDITING', false);
    commit('SET_EDITOR_COMMENTS', {
      generalDetails: '',
      involvement: '',
      activities: '',
      stumpage: '',
    });
  },
  setEditorComment({ commit }, { section, comment }) {
    commit('SET_EDITOR_COMMENT', { section, comment });
  },
  async adminUpdateGeneralDetails({ state, dispatch }, { data, submittedCallback }) {
    const payload = { ...data, editorComment: state.editorComments.generalDetails };
    await api.patch(`/landownerReport/${state.landownerReport.id}/adminUpdateGeneralDetails`, payload)
      .then(async (response) => {
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      });
  },
  async adminUpdateInvolvement({ state, dispatch }, { data, submittedCallback }) {
    const payload = { ...data, editorComment: state.editorComments.involvement };
    await api.patch(`/landownerReport/${state.landownerReport.id}/adminUpdateInvolvement`, payload)
      .then(async (response) => {
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      });
  },
  async adminUpdateActivities({ state, commit, dispatch }, { data, submittedCallback }) {
    const payload = { ...data, editorComment: state.editorComments.activities };
    await api.patch(`/landownerReport/${state.landownerReport.id}/adminUpdateActivities`, payload)
      .then(async (response) => {
        commit('SET_ACTIVITIES_ERRORS', []);
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      })
      .catch((error) => {
        commit('SET_ACTIVITIES_ERRORS', error.response.data);
        throw error;
      });
  },
  async adminUpdateStumpages({ state, commit, dispatch }, { data, submittedCallback }) {
    const payload = { ...data, editorComment: state.editorComments.stumpage };
    await api.patch(`/landownerReport/${state.landownerReport.id}/adminUpdateStumpages`, payload)
      .then(async (response) => {
        commit('SET_STUMPAGE_ERRORS', []);
        await dispatch('getLandownerReportNoReload', state.landownerReport.id);
        if (submittedCallback) submittedCallback(response.data);
      })
      .catch((error) => {
        commit('SET_STUMPAGE_ERRORS', Object.values(error.response.data).reduce((a, b) => a.concat(b)));
        throw error;
      });
  },
  async adminValidate({ commit, state }) {
    commit('SET_ERRORS_LOADING', true);
    await api.get(`landownerReport/${state.landownerReport.id}/adminValidate`)
      .then((response) => {
        commit('SET_ERRORS', response.data);
        commit('SET_ERRORS_LOADING', false);
      });
  },
  async adminSubmitEdit({ commit, dispatch }, id) {
    await api.post(`/landownerReport/${id}/adminSubmitEdit`)
      .then(async () => {
        await dispatch('getLandownerReportNoReload', id);
        commit('SET_ADMIN_EDITING', false);
        commit('SET_SHOW_SUBMITTED_MODAL', true);
      });
  },
  async getArchivedOriginal(_context, id) {
    const response = await api.get(`/landownerReport/${id}/archivedOriginal`);
    return response.data;
  },
};

const mutations = {
  SET_LANDOWNER_REPORT(state, landownerReport) {
    state.landownerReport = landownerReport;
  },
  UPDATE_LANDOWNER_REPORT_LOCAL(state, data) {
    state.landownerReport = { ...state.landownerReport, ...data };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ACTIVITIES_ERRORS(state, errors) {
    state.activitiesErrors = errors;
  },
  SET_STUMPAGE_ERRORS(state, errors) {
    state.stumpageErrors = errors;
  },
  SET_SHOW_SUBMITTED_MODAL(state, show) {
    state.showSubmittedModal = show;
  },
  SET_ERRORS(state, errors) {
    state.errors = errors;
  },
  SET_ERRORS_LOADING(state, loading) {
    state.errorsLoading = loading;
  },
  SET_ADMIN_EDITING(state, editing) {
    state.isAdminEditing = editing;
  },
  SET_EDITOR_COMMENTS(state, comments) {
    state.editorComments = comments;
  },
  SET_EDITOR_COMMENT(state, { section, comment }) {
    state.editorComments[section] = comment;
  },
};

const localGetters = {
  anyErrors(state) {
    return Object.values(state.errors).some(error => error.length);
  },
  started(state) {
    return (state.landownerReport.statusId === 1 || state.landownerReport.statusId === 3);
  },
  complete(state) {
    return (state.landownerReport.statusId === 2 || state.landownerReport.statusId === 4 || state.landownerReport.statusId === 5);
  },
  adminEditing(state) {
    return state.isAdminEditing;
  },
};

export default {
  actions,
  mutations,
  getters: localGetters,
  state: localState,
  namespaced: true,
};
